import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import { fetchMenuById } from "../../api/menuApi";
import GenerateAReportModal from "../ModalComponents/GenerateAReportModal";
import { getAllFiles, getInputFile, getReport } from "../../api/reportApi";
import "../../css/Reports.css";

/**
 * The Reports component represents the restaurant's reports page.
 *
 * @returns {JSX.Element} The rendered Reports component.
 */
const Reports = () => {
  const frontendPath = window.location.pathname;
  const menuId = JSON.parse(localStorage.getItem("restaurant")).menu.id;
  const [menuItems, setMenuItems] = useState([]);
  const { restaurantId } = useParams();
  const [isGenerateAReportOpen, setIsGenerateAReportOpen] = useState(false);

  const openGenerateAReport = useCallback(
    () => setIsGenerateAReportOpen(true),
    []
  );
  const closeGenerateAReport = useCallback(
    () => setIsGenerateAReportOpen(false),
    []
  );

  const handlePdfDownload = async (filename) => {
    try {
      const response = await getReport(frontendPath, filename);
      const blob = await response.blob();
      const fileurl = window.URL.createObjectURL(blob);
      const contentDisposition = response.headers.get("content-disposition");
      const filenameMatch =
        contentDisposition && contentDisposition.match(/filename="(.+)"/);
      const downloadFilename = filenameMatch ? filenameMatch[1] : "report.pdf";

      const link = document.createElement("a");
      link.href = fileurl;
      link.download = downloadFilename;
      link.click();
    } catch (error) {
      console.error("Error in fetching report:", error);
    }
  };

  const handleXlsxDownload = async (filename) => {
    try {
      const response = await getInputFile(frontendPath, filename);
      const blob = await response.blob();
      const fileurl = window.URL.createObjectURL(blob);
      const contentDisposition = response.headers.get("content-disposition");
      const filenameMatch =
        contentDisposition && contentDisposition.match(/filename="(.+)"/);
      const downloadFilename = filenameMatch
        ? filenameMatch[1]
        : "inputFile.xlsx";

      const link = document.createElement("a");
      link.href = fileurl;
      link.download = downloadFilename;
      link.click();
    } catch (error) {
      console.error("Error in fetching report:", error);
    }
  };

  const [reports, setReports] = useState([]);
  useEffect(() => {
    const fetchReports = async () => {
      try {
        const response = await getAllFiles(restaurantId, frontendPath);
        const reportData = await response.json();
        const formattedReports = Object.entries(reportData).map(
          ([excelFile, pdfFile]) => ({
            excelFile,
            pdfFile,
          })
        );
        setReports(formattedReports);
      } catch (error) {
        console.error("Error fetching reports:", error);
      }
    };

    fetchReports();
  }, [restaurantId, frontendPath, isGenerateAReportOpen]);

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const menuData = await fetchMenuById(menuId, frontendPath);
        setMenuItems(
          menuData.sections.flatMap((section) =>
            section.items.map((item) => item.name)
          )
        );
      } catch (error) {
        console.error("Error fetching menu:", error);
      }
    };

    fetchMenuItems();
  }, [setMenuItems, menuId, frontendPath]);

  const getCurrentWeekDates = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 (sunday) to 6 (saturday)

    // adjust so monday is the start of the week for reports
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(today.setDate(diff));

    // generate week dates (monday to sunday)
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      return {
        dayName: date.toLocaleDateString("en-US", { weekday: "long" }), // monday, tuesday, etc.
        formattedDate: date.toISOString().split("T")[0], // YYYY-MM-DD
      };
    });
  };

  const handleGetTemplate = () => {
    // print a template for the current week
    const weekDates = getCurrentWeekDates();

    // create sheet data for menu item sales
    const sheet1Data = [
      ["", ...weekDates.map((d) => d.dayName)],
      ["", ...weekDates.map((d) => d.formattedDate)],
      ...menuItems.map((item) => [item, ...Array(7).fill(0)]),
    ];

    // create sheet data for sales per employee (assuming 5 employees)
    let sheet2Data = [];
    const paymentMethods = [
      "Cash",
      "Debit",
      "Gift",
      "Mastercard",
      "Visa",
      "Tips",
    ];
    for (let i = 1; i <= 5; i++) {
      sheet2Data.push(["", ...weekDates.map((d) => d.dayName)]);
      sheet2Data.push([
        `frontOfHouse${i}`,
        ...weekDates.map((d) => d.formattedDate),
      ]);
      paymentMethods.forEach((paymentMethod) => {
        sheet2Data.push([paymentMethod, ...Array(7).fill("$0.00")]);
      });
      sheet2Data.push([""]);
    }

    // convert arrays to worksheet format
    const sheet1 = XLSX.utils.aoa_to_sheet(sheet1Data);
    const sheet2 = XLSX.utils.aoa_to_sheet(sheet2Data);

    // create a new workbook and append sheets to the workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet1, "menu item sales");
    XLSX.utils.book_append_sheet(workbook, sheet2, "sales per employee");

    // download the workbook with the name "template_YYYY_MM_DD.xlsx"
    XLSX.writeFile(
      workbook,
      `template_${weekDates[0].formattedDate.replaceAll("-", "_")}.xlsx`
    );
  };

  return (
    <div className="Reports-container">
      <div className="Reports">
        <div className="Reports-header">
          <h1 className="Reports-title">reports</h1>
          <button
            className="Reports-get-template-btn"
            onClick={handleGetTemplate}
          >
            get template
          </button>
        </div>
        <p className="Reports-description">
          download our .xlsx template to easily track your restaurant's data
          throughout the week. at the end of the week, upload the completed
          file, and we'll generate a detailed report based on your inputs.
        </p>
        <button
          className="Reports-generate-a-report-btn"
          onClick={openGenerateAReport}
        >
          + generate a report
        </button>
        <div className="Reports-table-container">
          <table className="Reports-table">
            <thead>
              <tr>
                <th>report name</th>
                <th>xlsx file</th>
                <th>download</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => {
                return (
                  <tr key={report.pdfFile}>
                    <td>{report.pdfFile}</td>
                    <td
                      className="clickable"
                      onClick={() => handleXlsxDownload(report.excelFile)}
                    >
                      {report.excelFile}
                    </td>
                    <td>
                      <button
                        className="Reports-download-btn"
                        onClick={() => handlePdfDownload(report.pdfFile)}
                      >
                        <FontAwesomeIcon icon={faDownload} size="lg" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {isGenerateAReportOpen && (
          <GenerateAReportModal
            isOpen={isGenerateAReportOpen}
            onRequestClose={closeGenerateAReport}
            width="35%"
            onUpload={closeGenerateAReport}
          />
        )}
      </div>
    </div>
  );
};

export default Reports;
